import React, { useEffect, useRef, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useAccount } from '../../context/AccountContext';
import {
  CREATE_COWORK_MCP_CONNECTOR,
  ROTATE_COWORK_MCP_CREDENTIALS,
  UPDATE_ACCOUNT,
  UPDATE_PUBLIC_SITE,
} from '../../graphql/mutations';
import { AGENT_GUIDES, AgentGuide, ConnectionValue, VALUE_LABELS } from './agentGuides';

/**
 * First-run setup for a TinyCMS account, shown over the app until the site
 * has an address and an AI agent connection:
 *
 *   1. Your site — its name and free address (yourname.tinycms.ai).
 *   2. Your agent — the MCP connection is created on the spot, and each agent
 *      (Claude, Grok, ChatGPT) has a how-to popup filled in with this site's
 *      own address and credentials.
 *   3. Done — what to ask for first.
 *
 * "Set up later" hides it for this account on this browser; Settings →
 * Connections and Site do the same jobs any time.
 */

const ONBOARDING_STATE = gql`
  query TinycmsOnboardingState($id: ID!) {
    account(id: $id) {
      id
      name
      publicSlug
      publicSiteUrl
    }
    coworkMcpConnector(accountId: $id) {
      mcpUrl
      hasCredentials
    }
  }
`;

interface Credentials {
  mcpUrl: string;
  clientId: string;
  clientSecret: string;
}

const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,62}$/;
const dismissKey = (accountId: string) => `tinycms.onboarding.dismissed.${accountId}`;

function readDismissed(accountId?: string) {
  if (!accountId) return false;
  try {
    return localStorage.getItem(dismissKey(accountId)) === '1';
  } catch {
    return false;
  }
}

function writeDismissed(accountId: string) {
  try {
    localStorage.setItem(dismissKey(accountId), '1');
  } catch {
    /* storage unavailable: it just shows again next time */
  }
}

/** "Rosa's Bakery" → "rosas-bakery". */
function toSlug(name: string) {
  return name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63);
}

function CopyValue({
  label,
  value,
  secret,
  prose,
}: {
  label: string;
  value: string;
  secret?: boolean;
  /** A sentence to copy, not a code value: shown in the body font. */
  prose?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="ink-onb-value">
      <div className="ink-onb-value-label">{label}</div>
      <div className="ink-onb-value-row">
        <code
          className="ink-onb-code"
          data-secret={secret ? 'true' : undefined}
          data-prose={prose ? 'true' : undefined}
        >
          {value}
        </code>
        <button
          type="button"
          className="ink-btn ink-btn-quiet"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(value);
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            } catch {
              /* clipboard unavailable: the value is on screen to select */
            }
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

function GuidePopup({
  guide,
  creds,
  onClose,
}: {
  guide: AgentGuide;
  creds: Credentials;
  onClose: () => void;
}) {
  const valueOf: Record<ConnectionValue, string> = {
    url: creds.mcpUrl,
    clientId: creds.clientId,
    clientSecret: creds.clientSecret,
    // The Client ID is also a bearer token for agents that take one.
    token: creds.clientId,
    authUrl: `${creds.mcpUrl}/oauth/authorize`,
    tokenUrl: `${creds.mcpUrl}/oauth/token`,
    scope: 'mcp',
    vscodeConfig: JSON.stringify(
      {
        servers: {
          tinycms: {
            type: 'http',
            url: creds.mcpUrl,
            headers: { Authorization: `Bearer ${creds.clientId}` },
          },
        },
      },
      null,
      2
    ),
  };
  return (
    <div
      className="ink-modal-back"
      style={{ zIndex: 95 }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="ink-modal ink-onb-guide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="onb-guide-title"
      >
        <div className="ink-modal-title" id="onb-guide-title">
          Connect {guide.name}
        </div>
        <ol className="ink-onb-steps">
          {guide.steps.map((step, i) => (
            <li key={i}>
              <div>{step.text}</div>
              {step.values?.map((v) => (
                <CopyValue
                  key={v}
                  label={VALUE_LABELS[v]}
                  value={valueOf[v]}
                  secret={v === 'clientSecret' || v === 'token' || v === 'vscodeConfig'}
                />
              ))}
            </li>
          ))}
        </ol>
        {guide.note ? <p className="ink-site-hint">{guide.note}</p> : null}
        <div className="ink-modal-actions">
          <button type="button" className="ink-btn ink-btn-primary" onClick={onClose} autoFocus>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

export function CmsOnboarding() {
  const navigate = useNavigate();
  const { accountId, refetchAccounts } = useAccount();
  const { data, loading, refetch } = useQuery(ONBOARDING_STATE, {
    variables: { id: accountId },
    skip: !accountId,
    fetchPolicy: 'network-only',
  });
  const [dismissed, setDismissed] = useState(() => readDismissed(accountId));
  const [step, setStep] = useState<1 | 2 | 3 | null>(null);

  // Step 1
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [siteError, setSiteError] = useState('');
  const [updateAccount] = useMutation(UPDATE_ACCOUNT);
  const [updatePublicSite, { loading: savingSite }] = useMutation(UPDATE_PUBLIC_SITE);

  // Step 2
  const [creds, setCreds] = useState<Credentials | null>(null);
  const [agentError, setAgentError] = useState('');
  const [guide, setGuide] = useState<AgentGuide | null>(null);
  // Create the connection once per account, even if the effect runs twice.
  const createdFor = useRef<string | null>(null);
  const [createConnector, { loading: creating }] = useMutation(CREATE_COWORK_MCP_CONNECTOR);
  const [rotateConnector, { loading: rotating }] = useMutation(ROTATE_COWORK_MCP_CREDENTIALS);

  const account = data?.account;
  const connector = data?.coworkMcpConnector;

  useEffect(() => {
    setDismissed(readDismissed(accountId));
    setStep(null);
    setCreds(null);
  }, [accountId]);

  // Decide where to start once the account's state is known.
  useEffect(() => {
    if (loading || !account || step !== null) return;
    if (!account.publicSlug) {
      // A new account is named after its email; don't offer that as a site name.
      const start = account.name && !account.name.includes('@') ? account.name : '';
      setName(start);
      setSlug(toSlug(start));
      setStep(1);
    } else if (!connector) {
      setStep(2);
    }
  }, [loading, account, connector, step]);

  // Step 2 creates the connection as soon as it opens.
  useEffect(() => {
    if (step !== 2 || creds || creating || connector || !accountId) return;
    if (createdFor.current === accountId) return;
    createdFor.current = accountId;
    setAgentError('');
    createConnector({ variables: { accountId, label: 'AI agent' } })
      .then(({ data: d }) => setCreds(d?.createCoworkMcpConnector || null))
      .catch((err) => {
        createdFor.current = null;
        setAgentError(err instanceof Error ? err.message : 'Could not connect');
      });
  }, [step, creds, creating, connector, accountId, createConnector]);

  if (!accountId || dismissed || step === null) return null;

  const close = () => {
    writeDismissed(accountId);
    setDismissed(true);
  };

  const saveSite = async (e: React.FormEvent) => {
    e.preventDefault();
    setSiteError('');
    const handle = slug.trim().toLowerCase();
    if (!name.trim()) return setSiteError('Give your site a name');
    if (!SLUG_RE.test(handle)) {
      return setSiteError(
        'Use 2 to 63 lowercase letters, numbers and dashes, starting with a letter or number'
      );
    }
    try {
      await updateAccount({
        variables: { id: accountId, input: { id: accountId, name: name.trim() } },
      });
      await updatePublicSite({
        variables: {
          accountId,
          input: { publicSlug: handle, publicTitle: name.trim(), publicEnabled: true },
        },
      });
      await Promise.all([refetch(), refetchAccounts()]);
      setStep(connector ? 3 : 2);
    } catch (err) {
      const message = err instanceof Error ? err.message : '';
      setSiteError(
        /already in use/i.test(message)
          ? `${handle}.tinycms.ai is taken. Try another.`
          : /reserved/i.test(message)
            ? `${handle} is reserved. Try another.`
            : message || 'Could not save your site'
      );
    }
  };

  const newSecret = async () => {
    setAgentError('');
    try {
      const { data: d } = await rotateConnector({ variables: { accountId, label: 'AI agent' } });
      setCreds(d?.rotateCoworkMcpCredentials || null);
    } catch (err) {
      setAgentError(err instanceof Error ? err.message : 'Could not make new connection details');
    }
  };

  const steps = ['Your site', 'Your agent', 'Done'];

  return (
    <div className="ink-modal-back" style={{ zIndex: 90 }}>
      <div
        className="ink-modal ink-onb"
        role="dialog"
        aria-modal="true"
        aria-labelledby="onb-title"
      >
        <ol className="ink-onb-progress" aria-label="Setup steps">
          {steps.map((label, i) => (
            <li key={label} data-state={step > i + 1 ? 'done' : step === i + 1 ? 'now' : 'next'}>
              <span>{step > i + 1 ? '✓' : i + 1}</span>
              {label}
            </li>
          ))}
        </ol>

        {step === 1 ? (
          <form onSubmit={saveSite}>
            <div className="ink-modal-title" id="onb-title">
              Name your site
            </div>
            <p className="ink-modal-copy">
              You can change the name any time. Your free address is where people find you until you
              connect your own.
            </p>
            <div style={{ display: 'grid', gap: 14, marginTop: 18 }}>
              <label className="ink-site-label">
                Site name
                <input
                  className="ink-site-input"
                  value={name}
                  placeholder="Rosa’s Bakery"
                  autoFocus
                  onChange={(e) => {
                    setName(e.target.value);
                    if (!slugEdited) setSlug(toSlug(e.target.value));
                  }}
                />
              </label>
              <label className="ink-site-label">
                Free address
                <span className="ink-onb-address">
                  <input
                    className="ink-site-input"
                    value={slug}
                    placeholder="rosas-bakery"
                    autoCapitalize="none"
                    spellCheck={false}
                    onChange={(e) => {
                      setSlugEdited(true);
                      setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''));
                    }}
                  />
                  <span>.tinycms.ai</span>
                </span>
              </label>
            </div>
            {siteError ? <p className="ink-onb-error">{siteError}</p> : null}
            <div className="ink-modal-actions" style={{ justifyContent: 'space-between' }}>
              <button type="button" className="ink-bill-link" onClick={close}>
                Set up later
              </button>
              <button type="submit" className="ink-btn ink-btn-primary" disabled={savingSite}>
                {savingSite ? 'Saving…' : 'Continue'}
              </button>
            </div>
          </form>
        ) : null}

        {step === 2 ? (
          <div>
            <div className="ink-modal-title" id="onb-title">
              Connect your AI agent
            </div>
            <p className="ink-modal-copy">
              Your agent builds your pages and posts through this connection. You still approve
              everything before it goes live.
            </p>
            {creds ? (
              <>
                <div className="ink-onb-values">
                  <CopyValue label="Server address" value={creds.mcpUrl} />
                  <CopyValue label="Client ID" value={creds.clientId} secret />
                  <CopyValue label="Client secret" value={creds.clientSecret} secret />
                </div>
                <p className="ink-site-hint" style={{ paddingBottom: 6 }}>
                  Keep these private, like a password. The secret is shown only now; you can make a
                  new one in Settings → Connections.
                </p>
                <div className="ink-onb-agents">
                  {AGENT_GUIDES.map((g) => (
                    <button
                      key={g.id}
                      type="button"
                      className="ink-onb-agent"
                      onClick={() => setGuide(g)}
                    >
                      <span className="ink-onb-agent-name">How to connect {g.name}</span>
                      <span className="ink-onb-agent-blurb">{g.blurb}</span>
                    </button>
                  ))}
                </div>
              </>
            ) : connector && !creating ? (
              <div style={{ marginTop: 16 }}>
                <p className="ink-modal-copy">
                  This site already has an agent connection. Its secret was shown when it was made.
                  Make new connection details to connect another agent; the old ones stop working.
                </p>
                <button
                  type="button"
                  className="ink-btn"
                  style={{ marginTop: 12 }}
                  disabled={rotating}
                  onClick={() => void newSecret()}
                >
                  {rotating ? 'Making…' : 'Make new connection details'}
                </button>
              </div>
            ) : (
              <p className="ink-modal-copy">Setting up your connection…</p>
            )}
            {agentError ? <p className="ink-onb-error">{agentError}</p> : null}
            <div className="ink-modal-actions" style={{ justifyContent: 'space-between' }}>
              <button type="button" className="ink-bill-link" onClick={close}>
                Set up later
              </button>
              <button
                type="button"
                className="ink-btn ink-btn-primary"
                disabled={!creds && !connector}
                onClick={() => setStep(3)}
              >
                I’ve connected my agent
              </button>
            </div>
          </div>
        ) : null}

        {step === 3 ? (
          <div>
            <div className="ink-modal-title" id="onb-title">
              You’re set
            </div>
            <p className="ink-modal-copy">
              Ask your agent for your first page, in your own words. It shows you a private preview,
              and nothing goes live until you say yes.
            </p>
            <CopyValue
              prose
              label="Try asking"
              value={`Make a home page for my site ${account?.publicSiteUrl || ''}. It's for [what you do]. Keep it short and friendly, then show me a preview.`.replace(
                /\s+\./,
                '.'
              )}
            />
            <div className="ink-modal-actions">
              <button
                type="button"
                className="ink-btn ink-btn-primary"
                onClick={() => {
                  close();
                  navigate('/today');
                }}
              >
                Go to Today
              </button>
            </div>
          </div>
        ) : null}
      </div>
      {guide && creds ? (
        <GuidePopup guide={guide} creds={creds} onClose={() => setGuide(null)} />
      ) : null}
    </div>
  );
}
